import { TextField, InputAdornment, OutlinedInput, Fab, Checkbox, Box, Grid, FormControlLabel, IconButton, Button} from '@material-ui/core';
import { Add, Refresh, Search } from '@material-ui/icons';
import {DataGrid} from '@material-ui/data-grid';
import { makeStyles } from '@material-ui/core/styles';
import React, {useEffect, useState, useCallback, useRef, useMemo} from 'react';
import _ from 'lodash';
import moment from 'moment';
import { getUserPreference, setHideAssociatedOrders, setHideSubReferrer } from '../../../../utilities/common';
import { useTranslation } from 'react-i18next';
import { setBatchSelectionConfig, showAlertSnackbar } from '../../../../reducers/uiSlice';
import { useDispatch } from 'react-redux';
import { useApiRef } from '../../../../utilities/hooks';
import TableTooltip from '../../../Common/TableTooltip';
import AddEditComment from '../../../../pages/BatchManagement/AddEditComment';

const useStyles = makeStyles((theme)=> ({
    root: {
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%'
    }
}))

function SelectBatchTable(props){
    
    const dispatch = useDispatch();
    const classes = useStyles();
    const {t} = useTranslation('mapManagement');
    const userPref = getUserPreference();

    const [searchKeyword, setSearchKeyword] = useState("");
    const [filter, setFilter] = useState({
        count: userPref.selectBatchCountFilter || 0,
        // count: userPref.selectBatchCountFilter || 20,
        dateAfter: moment().subtract(30, 'days')
    })
    const [data, setData] = useState([])
    const [selectTable, setSelectTable] = useState([])
    const [sortOptions, setSortOptions] = useState([{field: "eta",sort: 'desc'}])
    
    const delaySearch = useCallback(_.debounce(value=>setSearchKeyword(value), 350), [])
    const delayFilter = useCallback(_.debounce(value=>setFilter(value), 350), [])

    const [addEditCommentOpen, setAddEditCommentOpen] = useState(false);
    const [addEditComment, setAddEditComment] = useState('');
    const [addEditCommentBatch, setAddEditCommentBatch] = useState('');
    
    useEffect(()=> {
        let filteredData = filterData(props.batchList, filter)
        let filteredSearch = filterSearch(filteredData)
        setData(filteredSearch)
    }, [filter, props.batchList, searchKeyword])

    useEffect(() => {
        props.fetchTimestamp(filter.dateAfter);
    }, [filter])

    useEffect(()=> {
        if(props.batchList && props.batchList.length > 0){
            setSelectTable(getBatchIdsByReferer())
        }
    }, [props.batchList])

    const _columns = useMemo(()=>[
        {field: 'referer', headerName: t('batch_number_header'), flex: 1.5, sortable: false},
        {field: 'batch_type', headerName: t('batch_type_header'), flex: 0.5, sortable: false, valueFormatter: (params)=> params.value === 3 ? '2nd' : ''},
        {field: 'refer_count', headerName: t('order_count_header'), type: 'number', width: 100},
        {field: 'eta', headerName: t('common:date'), type: 'date', flex: 1,
            valueFormatter: (params)=> moment.unix(params.value).format("MMM DD, YYYY")},
        {field: 'dispatch_no', headerName: t('dispatch_number'), flex: 1, sortable: false},
        {
            field: 'description', 
            headerName: t('common:description'), 
            flex: 1, 
            sortable: false,
            renderCell: (params) => (
                <div
                    onClick={() => {
                        setAddEditComment(params.value ?? '');
                        setAddEditCommentBatch(params.row.referer);
                        setAddEditCommentOpen(true);
                    }}
                >
                    <TableTooltip 
                        value={params.value || 'N/A'}
                    />
                </div>
            )
        },
        {field: 'refer_type', hide: true}
    ]) 

    const {apiRef, columns} = useApiRef(_columns);

    return (
        <div className={classes.root}>
            <form style={{flexDirection: 'row', paddingBottom: 16}}>
                <Grid container spacing={1}>
                    <Grid item xs={3}>
                        <TextField 
                            id="filter-date"
                            label={t('common:from_date')}
                            type="date"
                            variant="outlined"
                            defaultValue={filter.dateAfter.format("YYYY-MM-DD")}
                            onChange={handleFilterDateChange}
                            style={{paddingRight: 16}}
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <TextField 
                            id="filter-count"
                            label={t('minimum_count')}
                            type='number'
                            variant='outlined'
                            defaultValue={filter.count}
                            onChange={handleFilterCountChange}
                            style={{width: 150}}
                        />
                    </Grid>
                    <Grid item xs={2} />
                    <Grid item xs={5}>
                        <OutlinedInput 
                            startAdornment={
                                <InputAdornment position='start'>
                                    <Search />
                                </InputAdornment>
                            }
                            placeholder={t('common:search')}
                            onChange={handleSearchChange}
                            style={{float: 'right'}}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <FormControlLabel
                            control={
                                <Checkbox 
                                    defaultChecked={userPref.hideAssociatedOrders} 
                                    onClick={(event)=>{
                                        dispatch(setBatchSelectionConfig({isHideAssociated: event.target.checked}))
                                        setHideAssociatedOrders(event.target.checked)
                                    }}
                                />}
                            label={t('hide_associated_orders')}
                        />
                        {/* <FormControlLabel
                            control={
                                <Checkbox 
                                    defaultChecked={userPref.hideSubReferrer}
                                    onClick={(event)=>{
                                        dispatch(setBatchSelectionConfig({isHideSubReferrer: event.target.checked}))
                                        setHideSubReferrer(event.target.checked)
                                    }}
                                />}
                            label={t('hide_sub_referrer')}
                        /> */}
                    </Grid>
                    <Grid item xs={8}>
                        <Box m={1} style={{float: 'right'}}>
                            <Button 
                                color="primary" 
                                aria-label='add' 
                                size="small"
                                variant="outlined"
                                disabled={props.createSubDisabled}
                                onClick={handleCreateSubBatch}
                                startIcon={<Add />}
                                style={{
                                    marginRight: 4
                                }}
                            >
                                {t('sub_batch_short')}
                            </Button>
                            <IconButton
                                color="primary" 
                                aria-label='refresh' 
                                size="small"
                                onClick={_handleBatchListRefresh}
                                style={{
                                    marginLeft: 4
                                }}
                            >
                                <Refresh />
                            </IconButton>
                        </Box>
                    </Grid>
                </Grid>
            </form>
            <div style={{flexGrow: 1}}>
                <div style={{display: 'flex', height: '100%'}}>
                    <div style={{flexGrow: 1}}>
                        <DataGrid
                            columns={columns} 
                            rows={data}
                            sortModel={[{field: "eta",sort: 'desc'}]}
                            state={{
                                keyboard: {
                                  cell: null,
                                  columnHeader: null,
                                  isMultipleKeyPressed: false,
                                }}}
                            onSelectionModelChange={(newSelection) => {
                                const MAX = process.env.REACT_APP_MAX_BATCH_SELECT || 50
                                let isMaxReached = false
                                let selectionArray = newSelection
                                let sortedRowIds = apiRef.current.getSortedRowIds()

                                if(newSelection.length >= MAX && selectTable.length === 0){
                                    //If select all and more than max
                                    selectionArray = _.slice(sortedRowIds, 0, MAX)
                                } else if(newSelection.length <= MAX){
                                    selectionArray = newSelection
                                } else if(newSelection.length > MAX){
                                    selectionArray = _.intersection(selectionArray, selectTable)
                                    isMaxReached = true
                                }

                                if(selectionArray.length <= MAX){
                                    setSelectTable(selectionArray)
                                    props.onTableSelection(selectionArray)
                                } else {
                                    isMaxReached = true
                                }

                                if(isMaxReached){
                                    dispatch(showAlertSnackbar({title: "Batch Selection", message: `Max selection reached! Only ${MAX} orders are allowed`}))
                                }
                            }}
                            selectionModel={selectTable}
                            isRowSelectable={(params)=> params.row.refer_type !== -1}
                            checkboxSelection
                            pageSize={8}
                            rowsPerPageOptions={[8]}
                            disableColumnFilter
                            disableColumnMenu
                        />
                    </div>
                </div>
            </div>
            <AddEditComment
                openModal={addEditCommentOpen}
                onClose={() => {
                    setAddEditCommentOpen(false);
                    setAddEditComment('');
                }}
                commentOri={addEditComment}
                batches={addEditCommentBatch}
                warehouse={userPref.userCity}
                handleCommentUpdate={handleCommentUpdate}
            />
        </div>
    )

    function handleCommentUpdate(comment) {
        const dataNew = data.map((element) => {
            if(element.referer === addEditCommentBatch) {
                element['description'] = comment;
            }
            return element;
        });
        setData(dataNew);
    }

    function getSelectedItems(batchList, selectedIds){
        //This function is used to make sure selectionModel won't go crazy when items disappear but ids are still intact
        let result = _.map(selectedIds, (id)=>{
            if (_.findIndex(batchList, (batch) => batch.id === id) !== -1){
                return id
            }
        })
        return _.compact(result)
    }

    function filterData(batchList, filter){
        // return _.filter(batchList, function(batch){return batch.refer_count >= filter.count && batch.eta >= filter.dateAfter.unix()})
        return _.filter(batchList, function(batch){return batch.refer_count >= filter.count})
    }

    function filterSearch(array){
        let preserveChecked = []
        let keywordFilter = _.filter(array, (item)=> {
            return item.referer?.match(new RegExp(searchKeyword, 'i'))
        })
        let subReferer = _.filter(array, (item)=> {
            return item.dispatch_no?.match(new RegExp(searchKeyword, 'i'))
        })
        let filter = _.union(keywordFilter, subReferer)
        if(selectTable){
            preserveChecked = _.filter(array, (item)=> selectTable.includes(item.id))
        }
        return _.union(filter, preserveChecked)
    }

    function getBatchIdsByReferer(){
        let findBatches = _.intersectionWith(props.batchList, props.selectedBatches, (batch, referer) => batch.referer === referer)
        console.log(findBatches)
        return _.map(findBatches, 'id')
    }

    function handleCreateSubBatch(){
        if(props.selectedBatches){
            props.createSubBatch(props.selectedBatches)
        }
    }

    function _handleBatchListRefresh(){
        if(!props.isLoading){
            props.fetchBatchList()
        }
    }

    function handleSearchChange(event){
        delaySearch(event.target.value)
    }

    function handleFilterCountChange(event){
        delayFilter({...filter, count: event.target.value})
        window.localStorage.setItem('select_batch_count_filter', event.target.value)
    }

    function handleFilterDateChange(event){
        delayFilter({...filter, dateAfter: moment(event.target.value)})
    }
}

SelectBatchTable.defaultProps = {
    batchList: []
}

export default SelectBatchTable